import React, { useState, useEffect, useCallback } from "react";

//@MATERIAL
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";

//REDUX
import history from "store/history";

//COMPONENTS
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import CardFooter from "components/Card/CardFooter";
import Password from "components/Password";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle";

//FUNCTIONS
import api from "api/"
import apims from "apims/"
import { translate, customApp } from "functions/";

const useStyles = makeStyles(styles);

// Estados da validação de token
const TOKEN_STATES = {
  VALIDATING: 'validating',
  VALID: 'valid',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  ERROR: 'error'
};

export default function ActivateUser(props) {
  const query = new URLSearchParams(window.location.search);
  const [user, setUser] = useState({ cPassword: null, password: null });
  const [tokenState, setTokenState] = useState({
    status: TOKEN_STATES.VALIDATING,
    isLoading: false,
    error: null,
    userEmail: null
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const classes = useStyles();
  const token = query.get('token');

  console.log('🔄 [ActivateUser] Component loaded with token:', token?.substring(0, 8) + '...');

  const tokenValidation = useCallback(async () => {
    if (!token || token.length !== 36) {
      console.warn('❌ [ActivateUser] Invalid token format:', token);
      setTokenState({
        status: TOKEN_STATES.INVALID,
        isLoading: false,
        error: 'Token inválido ou ausente'
      });
      setTimeout(() => history.push('/'), 3000);
      return;
    }

    setTokenState(prev => ({ ...prev, status: TOKEN_STATES.VALIDATING, isLoading: true }));
    console.log('🔍 [ActivateUser] Validating token...');

    try {
      const response = await apims.post("/PasswordTokenValidation", {
        token: token
      });

      console.log('📨 [ActivateUser] Token validation response:', response.data);

      if (response.data?.result === true) {
        console.log('✅ [ActivateUser] Token is valid');
        setTokenState({
          status: TOKEN_STATES.VALID,
          isLoading: false,
          error: null,
          userEmail: response.data.userEmail
        });
      } else if (response.data?.code === 'TOKEN_EXPIRED') {
        console.warn('⏰ [ActivateUser] Token expired');
        setTokenState({
          status: TOKEN_STATES.EXPIRED,
          isLoading: false,
          error: 'Token expirado. Solicite uma nova recuperação de senha.'
        });
        setNotification({
          open: true,
          message: 'Token expirado. Redirecionando para nova solicitação...',
          severity: 'warning'
        });
        setTimeout(() => history.push('/'), 3000);
      } else {
        console.warn('❌ [ActivateUser] Token validation failed:', response.data);
        setTokenState({
          status: TOKEN_STATES.INVALID,
          isLoading: false,
          error: response.data?.error || 'Token inválido'
        });
        setTimeout(() => history.push('/'), 3000);
      }
    } catch (error) {
      console.error('🚨 [ActivateUser] Token validation error:', error);

      let errorMessage = 'Erro ao validar token';
      let status = TOKEN_STATES.ERROR;

      if (error.response?.data) {
        const backendError = error.response.data;
        errorMessage = backendError.error || 'Erro de validação';

        if (backendError.code === 'TOKEN_EXPIRED') {
          status = TOKEN_STATES.EXPIRED;
          errorMessage = 'Token expirado. Solicite uma nova recuperação.';
        } else if (backendError.code === 'TOKEN_INVALID') {
          status = TOKEN_STATES.INVALID;
          errorMessage = 'Token inválido ou não encontrado.';
        }
      }

      setTokenState({
        status: status,
        isLoading: false,
        error: errorMessage
      });

      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      setTimeout(() => history.push('/'), 3000);
    }
  }, [token]);

  useEffect(() => {
    tokenValidation();
  }, [tokenValidation]);

  const savePassword = useCallback(async () => {
    if (!user.password) {
      setNotification({
        open: true,
        message: 'Senha é obrigatória',
        severity: 'warning'
      });
      return;
    }

    setTokenState(prev => ({ ...prev, isLoading: true }));
    console.log('💾 [ActivateUser] Saving new password...');

    try {
      const response = await api.post('/user/new-password', {
        token: token,
        password: user.password,
      });

      console.log('✅ [ActivateUser] Password saved successfully:', response.data);

      if (response?.data?.token) {
        localStorage.setItem('TOKEN', response.data.token);

        setNotification({
          open: true,
          message: 'Senha alterada com sucesso! Redirecionando...',
          severity: 'success'
        });

        if (props.callback) {
          setTimeout(() => {
            history.push('/login');
          }, 2000);
        }
      } else {
        throw new Error('Token de resposta não encontrado');
      }
    } catch (error) {
      console.error('❌ [ActivateUser] Password save failed:', error);

      let errorMessage = 'Erro ao salvar senha';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setNotification({
        open: true,
        message: `${translate('$__invalidRequest', 'Requisição inválida')} | ${errorMessage}`,
        severity: 'error'
      });

      if (props.callback) {
        props.callback({
          fn: 'alert',
          message: `${translate('$__invalidRequest', 'Requisição inválida')} | ${translate('$__contactSupport', 'Entre em contato com o suporte')}`,
          variant: 'warning',
          persist: false,
          horizontal: 'center',
          confirm: null
        });
      }
    } finally {
      setTokenState(prev => ({ ...prev, isLoading: false }));
    }
  }, [user.password, token, props]);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Renderizar loading ou erro durante validação
  if (tokenState.status === TOKEN_STATES.VALIDATING) {
    return (
      <div className={classes.container} style={{ color: '#333' }}>
        <GridContainer style={{ justifyContent: "center", overflow: "auto" }}>
          <GridItem xl={4} lg={4} md={4} sm={12} xs={12}>
            <Card login>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="primary"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.15)",
                  borderRadius: '16px 16px 0 0',
                  color: '#ffffff'
                }}
              >
                <h4 className={classes.cardTitle} style={{ color: '#ffffff' }}>Validando Token</h4>
              </CardHeader>
              <CardBody style={{ textAlign: 'center', padding: '2rem' }}>
                <CircularProgress size={40} />
                <div style={{ marginTop: '1rem', color: '#444', fontWeight: 500 }}>
                  {translate('$__validatingToken', 'Verificando token de recuperação...')}
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  // Renderizar erro se token inválido/expirado
  if ([TOKEN_STATES.INVALID, TOKEN_STATES.EXPIRED, TOKEN_STATES.ERROR].includes(tokenState.status)) {
    return (
      <div className={classes.container} style={{ color: '#333' }}>
        <GridContainer style={{ justifyContent: "center", overflow: "auto" }}>
          <GridItem xl={4} lg={4} md={4} sm={12} xs={12}>
            <Card login>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="primary"
                style={{
                  background: "#f44336",
                  boxShadow: "0 4px 20px 0 rgba(244,67,54,0.14), 0 7px 10px -5px rgba(244,67,54,0.4)",
                  color: '#ffffff'
                }}
              >
                <h4 className={classes.cardTitle} style={{ color: '#ffffff' }}>
                  {tokenState.status === TOKEN_STATES.EXPIRED ? 'Token Expirado' : 'Token Inválido'}
                </h4>
              </CardHeader>
              <CardBody style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ color: '#444', marginBottom: '1rem', fontWeight: 500 }}>
                  {tokenState.error || 'Token não encontrado ou inválido'}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  Redirecionando para a página inicial...
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  // Renderizar formulário de nova senha (token válido)
  return (
    <>
      <div className={classes.container} style={{ color: '#333' }}>
        <GridContainer style={{ justifyContent: "center", overflow: "auto" }}>
          <GridItem xl={4} lg={4} md={4} sm={12} xs={12}>
            <form>
              <Card db="cards" login style={{
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.15)'
              }}>
                <CardHeader
                  className={`${classes.cardHeader} ${classes.textCenter}`}
                  color="primary"
                  style={{
                    background: customApp("ColumnTitleColor"),
                    boxShadow: "0 4px 20px 0 rgba(15,18,35,0.14), 0 7px 10px -5px rgba(15,18,35,0.4)",
                    color: '#ffffff'
                  }}
                >
                  <h4 className={classes.cardTitle} style={{ color: '#ffffff' }}>
                    {translate('$__createPassword', 'Criar Nova Senha')}
                  </h4>
                  {tokenState.userEmail && (
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '0.5rem', color: '#ffffff' }}>
                      {tokenState.userEmail}
                    </div>
                  )}
                </CardHeader>
                <CardBody style={{ color: '#333' }}>
                  <Password
                    onConfirmed={(v) => {
                      console.log('🔐 [ActivateUser] Password confirmed:', v ? 'YES' : 'NO');
                      console.log('🔐 [ActivateUser] Current user state:', user);
                      setUser(prevUser => {
                        const newUser = {
                          ...prevUser,
                          password: v
                        };
                        console.log('🔐 [ActivateUser] Updated user state:', newUser);
                        return newUser;
                      });
                    }}
                  />

                  {/* Debug info para desenvolvimento */}
                  {process.env.NODE_ENV === 'development' && (
                    <div style={{
                      marginTop: 16,
                      padding: 8,
                      background: '#f5f5f5',
                      borderRadius: 4,
                      fontSize: '0.8rem',
                      color: '#333'
                    }}>
                      <strong style={{ color: '#333' }}>Debug Info:</strong>
                      <div style={{ color: '#555' }}>Token Status: {tokenState.status}</div>
                      <div style={{ color: '#555' }}>User Email: {tokenState.userEmail || 'N/A'}</div>
                      <div style={{ color: '#555' }}>Token: {token?.substring(0, 8)}...</div>
                      <div style={{ color: user?.password ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                        Password Set: {user?.password ? '✅ YES' : '❌ NO'}
                      </div>
                      <div style={{ color: '#555' }}>Password Length: {user?.password?.length || 0}</div>
                    </div>
                  )}
                </CardBody>
                <CardFooter className={classes.justifyContentCenter} style={{ justifyContent: "center" }}>
                  <Button
                    style={{
                      backgroundColor: tokenState.isLoading ? 'gray' :
                        (!user?.password ? '#cccccc' : customApp("menu")),
                      color: "#FFF",
                      position: "relative",
                      float: "right",
                      padding: "12px 27px",
                      fontSize: "18px",
                      fontWeight: "bold",
                      display: (tokenState.isLoading || !user?.password) ? 'none' : 'inline-block'
                    }}
                    simple
                    size="lg"
                    block
                    onClick={user?.password ? savePassword : undefined}
                    disabled={tokenState.isLoading || !user?.password}
                  >
                    {tokenState.isLoading ? (
                      <CircularProgress
                        size={24}
                        style={{
                          color: "#FFF",
                        }}
                      />
                    ) : !user?.password ? (
                      translate('$__passwordInstructions', `*`, 'Digite e confirme sua nova senha')
                    ) : (
                      translate('$__save', `*`, 'Salvar Nova Senha')
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </form>
          </GridItem>
        </GridContainer>
      </div>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}